import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import {
    GET_STAFF_EVENT_ELIGIBLE_WORKER_IDS,
    GET_STAFF_EVENT_OPEN_SESSIONS,
} from "graphql/query/staff-event-worker-counts-gql";
import {
    getStaffWorkerSessionSummaries,
    STAFF_WORKER_SESSIONS_PAGE_SIZE,
    STAFF_WORKER_STATUS,
    STAFF_WORKERS_PAGE_SIZE,
} from "../helpers/staff-workers-api-helper";


const MAX_CONCURRENT_REQUESTS = 3;
const LOADING_COUNTS = { loading: true, error: null, counts: null };
const getID = (value) => (
    typeof value === "string" || Number.isFinite(value) ? String(value).trim() : ""
);
const incompleteResponse = () => new Error("Incomplete staff worker counts response");

// Keep the limit across context changes while earlier requests are still in flight.
const createRequestQueue = () => {
    let activeRequests = 0;
    const pending = [];
    const runTask = async (task) => {
        try
        {
            task.resolve(await task.run());
        }
        catch (error)
        {
            task.reject(error);
        }
        finally
        {
            activeRequests -= 1;
            drain();
        }
    };
    const drain = () => {
        while (activeRequests < MAX_CONCURRENT_REQUESTS && pending.length)
        {
            const task = pending.shift();
            if (!task.isCurrent())
            {
                task.resolve(undefined);
                continue;
            }

            activeRequests += 1;
            runTask(task);
        }
    };

    return (run, isCurrent) => new Promise((resolve, reject) => {
        pending.push({ run, isCurrent, resolve, reject });
        drain();
    });
};

const fetchCompletePages = async ({
    client, query, variables, model, first, concurrency = 1, validateRecord, isCurrent, request,
}) => {
    const records = new Map();
    let lastPage;
    let expectedTotal;
    const readPage = async (page) => {
        if (!isCurrent()) return;
        const result = await request(() => client.query({
            query,
            variables: { ...variables, first, page },
            fetchPolicy: "no-cache",
            errorPolicy: "none",
        }), isCurrent);
        if (!isCurrent()) return;

        const response = result?.data?.[ model ];
        const pagination = response?.paginatorInfo;
        if (result?.errors?.length || !Array.isArray(response?.data) || pagination?.currentPage !== page ||
            !Number.isInteger(pagination?.lastPage) || pagination.lastPage < page ||
            !Number.isInteger(pagination.total) || pagination.total < 0 ||
            (page > 1 && (pagination.lastPage !== lastPage || pagination.total !== expectedTotal)) ||
            (!response.data.length && page < pagination.lastPage))
        {
            throw incompleteResponse();
        }

        if (page === 1)
        {
            lastPage = pagination.lastPage;
            expectedTotal = pagination.total;
        }

        response.data.forEach((record) => {
            const id = getID(record?.id);
            if (!id || records.has(id) || !validateRecord(record)) throw incompleteResponse();
            records.set(id, record);
        });
    };

    await readPage(1);
    for (let page = 2; isCurrent() && page <= lastPage; page += concurrency)
    {
        const batch = Array.from(
            { length: Math.min(concurrency, lastPage - page + 1) },
            (_, index) => readPage(page + index)
        );
        await Promise.all(batch);
    }

    if (!isCurrent()) return undefined;
    if (records.size !== expectedTotal) throw incompleteResponse();
    return [ ...records.values() ];
};

const getCounts = (workerIDs, sessions) => {
    const counts = { checkedIn: 0, missedCheckout: 0, failedCheckout: 0 };
    const summaries = getStaffWorkerSessionSummaries(workerIDs, sessions);
    Object.values(summaries).forEach(({ status }) => {
        if (status === STAFF_WORKER_STATUS.checkedIn) counts.checkedIn += 1;
        if (status === STAFF_WORKER_STATUS.missedCheckout) counts.missedCheckout += 1;
        if (status === STAFF_WORKER_STATUS.failedCheckout) counts.failedCheckout += 1;
    });
    return counts;
};

const useStaffEventWorkerCounts = ({ events = [], contextKey = "" } = {}) => {
    const client = useApolloClient();
    const eventIDsKey = JSON.stringify([ ...new Set(
        (Array.isArray(events) ? events : []).map((event) => getID(event?.id)).filter(Boolean)
    ) ].sort());
    const eventIDs = useMemo(() => JSON.parse(eventIDsKey), [ eventIDsKey ]);
    const [ attempt, setAttempt ] = useState(0);
    const context = useMemo(() => ({ client, eventIDs, contextKey, attempt }), [ client, eventIDs, contextKey, attempt ]);
    const [ state, setState ] = useState(null);
    const requestQueue = useRef(null);
    if (!requestQueue.current) requestQueue.current = createRequestQueue();
    const retry = useCallback(() => {
        if (eventIDs.length) setAttempt((value) => value + 1);
    }, [ eventIDs ]);

    useEffect(() => {
        let current = true;
        const isCurrent = () => current;
        const request = requestQueue.current;
        setState(null);
        if (!eventIDs.length) return () => { current = false; };

        const publish = (eventID, entry) => {
            if (!isCurrent()) return;
            setState((previous) => ({
                context,
                byEventID: {
                    ...(previous?.context === context ? previous.byEventID : {}),
                    [ eventID ]: entry,
                },
            }));
        };
        const load = async () => {
            const eventSet = new Set(eventIDs);
            const sessions = await fetchCompletePages({
                client, request, isCurrent,
                query: GET_STAFF_EVENT_OPEN_SESSIONS,
                model: "workSessions",
                first: STAFF_WORKER_SESSIONS_PAGE_SIZE,
                concurrency: MAX_CONCURRENT_REQUESTS,
                variables: { where: {
                    column: "EE_ID", operator: "IN", value: eventIDs,
                    AND: [ { column: "CHECK_OUT_AT", operator: "IS_NULL" } ],
                } },
                validateRecord: (session) => (
                    eventSet.has(getID(session.ee_id)) && Boolean(getID(session.user_id)) &&
                    session.check_out_at === null &&
                    Object.prototype.hasOwnProperty.call(session, "getCheckOutStatus")
                ),
            });
            if (!isCurrent()) return;

            const sessionsByEvent = new Map(eventIDs.map((id) => [ id, [] ]));
            sessions.forEach((session) => sessionsByEvent.get(getID(session.ee_id)).push(session));
            let nextEventIndex = 0;
            const loadNextEvents = async () => {
                while (isCurrent() && nextEventIndex < eventIDs.length)
                {
                    const eventID = eventIDs[ nextEventIndex++ ];
                    const eventSessions = sessionsByEvent.get(eventID);
                    const candidateIDs = [ ...new Set(eventSessions.map((session) => getID(session.user_id))) ].sort();
                    if (!candidateIDs.length)
                    {
                        publish(eventID, { loading: false, error: null, counts: getCounts([], []) });
                        continue;
                    }

                    try
                    {
                        const candidates = new Set(candidateIDs);
                        const workers = await fetchCompletePages({
                            client, request, isCurrent,
                            query: GET_STAFF_EVENT_ELIGIBLE_WORKER_IDS,
                            model: "users",
                            first: STAFF_WORKERS_PAGE_SIZE,
                            variables: {
                                eeID: [ eventID ],
                                where: { column: "ID", operator: "IN", value: candidateIDs },
                            },
                            validateRecord: (worker) => candidates.has(getID(worker.id)),
                        });
                        if (!isCurrent()) return;
                        publish(eventID, {
                            loading: false, error: null,
                            counts: getCounts(workers.map((worker) => getID(worker.id)), eventSessions),
                        });
                    }
                    catch (error)
                    {
                        publish(eventID, { loading: false, error, counts: null });
                    }
                }
            };
            await Promise.all(Array.from(
                { length: Math.min(MAX_CONCURRENT_REQUESTS, eventIDs.length) }, loadNextEvents
            ));
        };

        load().catch((error) => {
            eventIDs.forEach((eventID) => publish(eventID, { loading: false, error, counts: null }));
        });
        return () => { current = false; };
    }, [ client, context, eventIDs ]);

    // A new page/filter must not render a previous generation's counts even once.
    const currentEntries = state?.context === context ? state.byEventID : {};
    return {
        byEventID: Object.fromEntries(eventIDs.map((id) => [ id, currentEntries[ id ] || LOADING_COUNTS ])),
        retry,
    };
};

export default useStaffEventWorkerCounts;
